import {
    Avatar,
    Box,
    Circle,
    HStack,
    Icon,
    IconButton,
    Link,
    Popover,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Portal,
    Text,
    VStack,
    useToast,
} from '@chakra-ui/react';
import React, { useCallback, useState } from 'react';
import { TbClock, TbMail, TbCircleCheck, TbCopy, TbCheck } from 'react-icons/tb';
import {
    MentionableUser,
    formatAwayUntil,
} from '../../hooks/useGetMentionableUsers';

function formatInactiveDuration(lastActive?: string): string {
    if (!lastActive) return 'Inactive';
    const ms = Date.now() - new Date(lastActive).getTime();
    if (!Number.isFinite(ms) || ms < 0) return 'Inactive';
    const hours = Math.floor(ms / (60 * 60 * 1000));
    if (hours < 1) return 'Inactive for under an hour';
    if (hours < 24) return `Inactive for ${hours} hour${hours === 1 ? '' : 's'}`;
    const days = Math.floor(hours / 24);
    return `Inactive for ${days} day${days === 1 ? '' : 's'}`;
}

export type PresenceAvatarUser = Pick<
    MentionableUser,
    | '_id'
    | 'displayName'
    | 'email'
    | 'presenceStatus'
    | 'statusNote'
    | 'awayEndsAt'
    | 'lastActive'
    | 'mayNotRespond'
    | 'mayNotRespondReason'
>;

interface PresenceAvatarProps {
    name: string;
    user?: PresenceAvatarUser | null;
    size?: 'xs' | 'sm' | 'md';
    bg?: string;
    /** When true, avatar is shown on the right (own messages) — popover opens left. */
    alignEnd?: boolean;
}

const PresenceAvatar = ({
    name,
    user,
    size = 'xs',
    bg = 'gray.400',
    alignEnd = false,
}: PresenceAvatarProps) => {
    const toast = useToast();
    const [copied, setCopied] = useState(false);

    const isAway = user?.presenceStatus === 'away' || user?.mayNotRespondReason === 'away';
    const isInactive = user?.mayNotRespondReason === 'inactive' || (!!user?.mayNotRespond && !isAway);
    const statusColor = isAway || isInactive ? 'red.400' : 'green.400';

    let statusLabel = 'Available';
    let statusDetail: string | undefined;
    if (isAway) {
        statusLabel = 'Away';
        const until = formatAwayUntil(user?.awayEndsAt);
        statusDetail = until ? `Until ${until}` : undefined;
        if (user?.statusNote?.trim()) {
            statusDetail = statusDetail
                ? `${statusDetail} · ${user.statusNote.trim()}`
                : user.statusNote.trim();
        }
    } else if (isInactive) {
        statusLabel = 'Inactive';
        statusDetail = formatInactiveDuration(user?.lastActive);
    }

    const copyEmail = useCallback(async (email: string, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        try {
            await navigator.clipboard.writeText(email);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
        } catch {
            toast({
                title: 'Could not copy email',
                status: 'error',
                duration: 2000,
                isClosable: true,
            });
        }
    }, [toast]);

    const dotSize = size === 'md' ? '12px' : size === 'sm' ? '10px' : '9px';

    const avatar = (
        <Box position="relative" display="inline-flex" flexShrink={0} cursor={user ? 'pointer' : 'default'}>
            <Avatar size={size} name={name} bg={bg} color="white" />
            {user && (
                <Circle
                    size={dotSize}
                    bg={statusColor}
                    borderWidth="2px"
                    borderColor="white"
                    position="absolute"
                    bottom="0"
                    right="0"
                />
            )}
        </Box>
    );

    if (!user) return avatar;

    return (
        <Popover
            trigger="hover"
            placement={alignEnd ? 'left-start' : 'right-start'}
            openDelay={200}
            closeDelay={200}
            isLazy
            gutter={8}
        >
            <PopoverTrigger>
                <Box as="button" type="button" lineHeight={0} aria-label={`${name} status`}>
                    {avatar}
                </Box>
            </PopoverTrigger>
            <Portal>
                <PopoverContent
                    w="280px"
                    maxW="280px"
                    bg="white"
                    borderColor="gray.200"
                    boxShadow="none"
                    borderRadius="lg"
                    zIndex={2200}
                    overflow="hidden"
                    _focus={{ outline: 'none' }}
                >
                    <PopoverBody p={3} overflow="hidden">
                        <HStack spacing={3} align="flex-start" mb={3} minW={0} w="100%">
                            <Box position="relative" flexShrink={0}>
                                <Avatar size="md" name={user.displayName || name} bg={bg} color="white" />
                                <Circle
                                    size="12px"
                                    bg={statusColor}
                                    borderWidth="2px"
                                    borderColor="white"
                                    position="absolute"
                                    bottom="0"
                                    right="0"
                                />
                            </Box>
                            <VStack align="flex-start" spacing={0} minW={0} flex={1} overflow="hidden">
                                <Text fontSize="sm" fontWeight="bold" color="gray.900" noOfLines={2} w="100%">
                                    {user.displayName || name}
                                </Text>
                                {user.email && (
                                    <HStack spacing={0.5} mt={0.5} minW={0} w="100%" overflow="hidden">
                                        <Icon as={TbMail as React.ElementType} w={3} h={3} color="gray.400" flexShrink={0} />
                                        <Link
                                            href={`mailto:${user.email}`}
                                            fontSize="xs"
                                            color="#0071E3"
                                            noOfLines={1}
                                            minW={0}
                                            flex={1}
                                            title={`Email ${user.email}`}
                                            _hover={{ textDecoration: 'underline' }}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {user.email}
                                        </Link>
                                        <IconButton
                                            aria-label={copied ? 'Copied' : 'Copy email'}
                                            icon={
                                                <Icon
                                                    as={(copied ? TbCheck : TbCopy) as React.ElementType}
                                                    w={3}
                                                    h={3}
                                                />
                                            }
                                            size="xs"
                                            variant="ghost"
                                            color={copied ? 'green.500' : 'gray.400'}
                                            minW="22px"
                                            h="22px"
                                            flexShrink={0}
                                            onClick={(e) => copyEmail(user.email!, e)}
                                            onMouseDown={(e) => e.preventDefault()}
                                            _hover={{ color: copied ? 'green.600' : '#0071E3', bg: 'gray.100' }}
                                        />
                                    </HStack>
                                )}
                            </VStack>
                        </HStack>

                        <HStack
                            spacing={2}
                            px={2.5}
                            py={2}
                            bg="gray.50"
                            borderRadius="md"
                            borderWidth="1px"
                            borderColor="gray.100"
                            align="flex-start"
                        >
                            <Icon
                                as={(isAway || isInactive ? TbClock : TbCircleCheck) as React.ElementType}
                                w={4}
                                h={4}
                                color={statusColor}
                                mt="1px"
                                flexShrink={0}
                            />
                            <VStack align="flex-start" spacing={0} minW={0}>
                                <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                    {statusLabel}
                                </Text>
                                {statusDetail && (
                                    <Text fontSize="xs" color="gray.500" lineHeight="1.35">
                                        {statusDetail}
                                    </Text>
                                )}
                            </VStack>
                        </HStack>
                    </PopoverBody>
                </PopoverContent>
            </Portal>
        </Popover>
    );
};

export default PresenceAvatar;
